<template>
  <div class="revenue-analytics">
    <div class="page-header">
      <h1>
        <i class="fas fa-chart-line" /> Tableau de Bord - Revenus
      </h1>
      <div class="header-actions">
        <router-link
          to="/expenses"
          class="btn-secondary"
        >
          <i class="fas fa-arrow-left" /> Retour
        </router-link>
      </div>
    </div>

    <!-- Date Range Picker -->
    <div class="date-filter-section">
      <div class="quick-filters">
        <button
          class="quick-filter-btn"
          :class="{ active: selectedQuickFilter === 'today' }"
          @click="setQuickFilter('today')"
        >
          Aujourd'hui
        </button>
        <button
          class="quick-filter-btn"
          :class="{ active: selectedQuickFilter === 'week' }"
          @click="setQuickFilter('week')"
        >
          Cette semaine
        </button>
        <button
          class="quick-filter-btn"
          :class="{ active: selectedQuickFilter === 'month' }"
          @click="setQuickFilter('month')"
        >
          Ce mois
        </button>
        <button
          class="quick-filter-btn"
          :class="{ active: selectedQuickFilter === 'last_month' }"
          @click="setQuickFilter('last_month')"
        >
          Mois dernier
        </button>
      </div>

      <div class="custom-date-range">
        <label>Date début</label>
        <input
          v-model="filters.start_date"
          type="date"
          @change="loadAnalytics"
        >
        <label>Date fin</label>
        <input
          v-model="filters.end_date"
          type="date"
          @change="loadAnalytics"
        >
      </div>
    </div>

    <!-- Summary Cards -->
    <div
      v-if="!loading"
      class="summary-cards"
    >
      <div class="summary-card revenue">
        <div class="card-icon">
          <i class="fas fa-dollar-sign" />
        </div>
        <div class="card-content">
          <div class="card-label">
            Revenu Total
          </div>
          <div class="card-value">
            {{ formatAmount(analytics.summary.total_amount) }} DT
          </div>
        </div>
      </div>

      <div class="summary-card profit">
        <div class="card-icon">
          <i class="fas fa-chart-line" />
        </div>
        <div class="card-content">
          <div class="card-label">
            Profit Total
          </div>
          <div class="card-value">
            {{ formatAmount(analytics.summary.total_profit) }} DT
          </div>
        </div>
      </div>

      <div class="summary-card count">
        <div class="card-icon">
          <i class="fas fa-file-invoice" />
        </div>
        <div class="card-content">
          <div class="card-label">
            Nombre de Transactions
          </div>
          <div class="card-value">
            {{ analytics.summary.count }}
          </div>
        </div>
      </div>

      <div class="summary-card average">
        <div class="card-icon">
          <i class="fas fa-calculator" />
        </div>
        <div class="card-content">
          <div class="card-label">
            Moyenne
          </div>
          <div class="card-value">
            {{ formatAmount(analytics.summary.average) }} DT
          </div>
        </div>
      </div>
    </div>

    <!-- Charts Section -->
    <div
      v-if="!loading"
      class="charts-section"
    >
      <!-- Category Breakdown -->
      <div class="chart-card">
        <h3>Répartition par Catégorie</h3>
        <apexchart
          v-if="categoryChartOptions.series.length > 0"
          type="donut"
          height="400"
          :options="categoryChartOptions.chartOptions"
          :series="categoryChartOptions.series"
        />
        <div
          v-else
          class="no-data-message"
        >
          Aucune donnée disponible
        </div>
      </div>

      <!-- Trend Chart -->
      <div class="chart-card">
        <h3>Évolution des Revenus</h3>
        <apexchart
          v-if="trendChartOptions.series.length > 0"
          type="area"
          height="400"
          :options="trendChartOptions.chartOptions"
          :series="trendChartOptions.series"
        />
        <div
          v-else
          class="no-data-message"
        >
          Aucune donnée disponible
        </div>
      </div>
    </div>

    <!-- Tables Section -->
    <div
      v-if="!loading"
      class="tables-section"
    >
      <!-- Status Breakdown -->
      <div class="table-card">
        <h3>Répartition par Statut</h3>
        <table class="analytics-table">
          <thead>
            <tr>
              <th>Statut</th>
              <th>Montant</th>
              <th>Transactions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <span class="status-badge confirmed">Confirmé</span>
              </td>
              <td class="amount-cell">
                {{ formatAmount((analytics.by_status.confirmed && analytics.by_status.confirmed.total) || 0) }} DT
              </td>
              <td>{{ (analytics.by_status.confirmed && analytics.by_status.confirmed.count) || 0 }}</td>
            </tr>
            <tr>
              <td>
                <span class="status-badge draft">Brouillon</span>
              </td>
              <td class="amount-cell">
                {{ formatAmount((analytics.by_status.draft && analytics.by_status.draft.total) || 0) }} DT
              </td>
              <td>{{ (analytics.by_status.draft && analytics.by_status.draft.count) || 0 }}</td>
            </tr>
            <tr>
              <td>
                <span class="status-badge cancelled">Annulé</span>
              </td>
              <td class="amount-cell">
                {{ formatAmount((analytics.by_status.cancelled && analytics.by_status.cancelled.total) || 0) }} DT
              </td>
              <td>{{ (analytics.by_status.cancelled && analytics.by_status.cancelled.count) || 0 }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Loading State -->
    <div
      v-if="loading"
      class="loading-state"
    >
      <i class="fas fa-spinner fa-spin" /> Chargement des données...
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import VueApexCharts from 'vue-apexcharts'

export default {
  name: 'RevenueAnalytics',

  components: {
    apexchart: VueApexCharts,
  },

  data() {
    return {
      loading: true,
      selectedQuickFilter: 'today',
      filters: {
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
      },
      analytics: {
        summary: {
          total_amount: 0,
          total_profit: 0,
          count: 0,
          average: 0,
        },
        by_category: [],
        by_vendor: [],
        by_status: {},
        trend: [],
      },
    }
  },

  computed: {
    categoryChartOptions() {
      if (!this.analytics.by_category || this.analytics.by_category.length === 0) {
        return {
          series: [],
          chartOptions: {},
        }
      }

      return {
        series: this.analytics.by_category.map(c => parseFloat(c.total)),
        chartOptions: {
          labels: this.analytics.by_category.map(c => c.category_name),
          colors: this.analytics.by_category.map(c => c.category_color),
          chart: {
            type: 'donut',
          },
          legend: {
            position: 'bottom',
          },
          dataLabels: {
            enabled: true,
            formatter(val) {
              return `${val.toFixed(1)}%`
            },
          },
          tooltip: {
            y: {
              formatter: value => `${parseFloat(value).toFixed(3)} DT`,
            },
          },
          plotOptions: {
            pie: {
              donut: {
                labels: {
                  show: true,
                  total: {
                    show: true,
                    label: 'Total',
                    formatter: () => `${this.formatAmount(this.analytics.summary.total_amount)} DT`,
                  },
                },
              },
            },
          },
        },
      }
    },

    trendChartOptions() {
      if (!this.analytics.trend || this.analytics.trend.length === 0) {
        return {
          series: [],
          chartOptions: {},
        }
      }

      return {
        series: [
          {
            name: 'Revenu',
            data: this.analytics.trend.map(t => parseFloat(t.total)),
          },
          {
            name: 'Profit',
            data: this.analytics.trend.map(t => parseFloat(t.profit)),
          },
        ],
        chartOptions: {
          chart: {
            type: 'area',
            toolbar: {
              show: false,
            },
          },
          colors: ['#10b981', '#3b82f6'],
          dataLabels: {
            enabled: false,
          },
          stroke: {
            curve: 'smooth',
            width: 2,
          },
          fill: {
            type: 'gradient',
            gradient: {
              opacityFrom: 0.6,
              opacityTo: 0.1,
            },
          },
          xaxis: {
            categories: this.analytics.trend.map(t => this.formatDate(t.date)),
          },
          yaxis: {
            labels: {
              formatter: value => `${value.toFixed(0)} DT`,
            },
          },
          tooltip: {
            y: {
              formatter: value => `${parseFloat(value).toFixed(3)} DT`,
            },
          },
          legend: {
            position: 'top',
          },
        },
      }
    },
  },

  mounted() {
    this.loadAnalytics()
  },

  methods: {
    async loadAnalytics() {
      this.loading = true
      try {
        const params = {
          start_date: this.filters.start_date,
          end_date: this.filters.end_date,
        }

        const response = await axios.get('/api/analytics/revenue/', { params })
        this.analytics = response.data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading analytics:', error)
        this.$toast.error('Erreur lors du chargement des analyses')
      } finally {
        this.loading = false
      }
    },

    setQuickFilter(filter) {
      this.selectedQuickFilter = filter
      const today = new Date()
      const [todayDate] = today.toISOString().split('T')

      switch (filter) {
        case 'today': {
          this.filters.start_date = todayDate
          this.filters.end_date = todayDate
          break
        }
        case 'week': {
          const weekStart = new Date(today)
          weekStart.setDate(today.getDate() - today.getDay())
          const [weekStartDate] = weekStart.toISOString().split('T')
          this.filters.start_date = weekStartDate
          this.filters.end_date = todayDate
          break
        }
        case 'month': {
          const monthStart = new Date(today.getFullYear(), today.getMonth(), 1)
          const [monthStartDate] = monthStart.toISOString().split('T')
          this.filters.start_date = monthStartDate
          this.filters.end_date = todayDate
          break
        }
        case 'last_month': {
          const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1)
          const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0)
          const [lastMonthStartDate] = lastMonthStart.toISOString().split('T')
          const [lastMonthEndDate] = lastMonthEnd.toISOString().split('T')
          this.filters.start_date = lastMonthStartDate
          this.filters.end_date = lastMonthEndDate
          break
        }
        default:
          break
      }

      this.loadAnalytics()
    },

    formatAmount(amount) {
      return parseFloat(amount || 0).toFixed(3)
    },

    formatDate(dateStr) {
      const date = new Date(dateStr)
      return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
    },
  },
}
</script>

<style scoped>
.revenue-analytics {
  padding: 24px;
  max-width: 100%;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-header h1 {
  font-size: 28px;
  color: #1f2937;
  margin: 0;
}

.page-header h1 i {
  color: #10b981;
  margin-right: 12px;
}

.btn-secondary {
  padding: 10px 20px;
  background: #f3f4f6;
  color: #4b5563;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.btn-secondary:hover {
  background: #e5e7eb;
}

/* Date Filters */
.date-filter-section {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.quick-filters {
  display: flex;
  gap: 8px;
}

.quick-filter-btn {
  padding: 8px 16px;
  background: #f3f4f6;
  color: #4b5563;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
  font-weight: 500;
}

.quick-filter-btn:hover {
  background: #e5e7eb;
}

.quick-filter-btn.active {
  background: #d1fae5;
  color: #047857;
  border-color: #10b981;
}

.custom-date-range {
  display: flex;
  gap: 12px;
  align-items: center;
}

.custom-date-range label {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

.custom-date-range input {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
}

/* Summary Cards */
.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
}

.summary-card {
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 16px;
}

.card-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.summary-card.revenue .card-icon {
  background: #d1fae5;
  color: #047857;
}

.summary-card.profit .card-icon {
  background: #dbeafe;
  color: #1e40af;
}

.summary-card.count .card-icon {
  background: #fef3c7;
  color: #92400e;
}

.summary-card.average .card-icon {
  background: #e0e7ff;
  color: #4338ca;
}

.card-label {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 4px;
}

.card-value {
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
}

/* Charts */
.charts-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 24px;
}

.chart-card {
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  width: 100%;
}

.chart-card.full-width {
  /* No longer needed but keeping for compatibility */
}

.chart-card h3 {
  margin: 0 0 20px 0;
  font-size: 18px;
  color: #1f2937;
  text-align: center;
}

/* Tables */
.tables-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 20px;
}

.table-card {
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.table-card h3 {
  margin: 0 0 16px 0;
  font-size: 18px;
  color: #1f2937;
}

.analytics-table {
  width: 100%;
  border-collapse: collapse;
}

.analytics-table th {
  text-align: left;
  padding: 12px;
  background: #f9fafb;
  color: #6b7280;
  font-weight: 600;
  font-size: 14px;
  border-bottom: 2px solid #e5e7eb;
}

.analytics-table td {
  padding: 12px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 14px;
}

.amount-cell {
  font-weight: 600;
  color: #10b981;
  font-family: 'Courier New', monospace;
}

.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.status-badge.confirmed {
  background: #d1fae5;
  color: #047857;
}

.status-badge.draft {
  background: #fef3c7;
  color: #92400e;
}

.status-badge.cancelled {
  background: #fee2e2;
  color: #991b1b;
}

.text-center {
  text-align: center;
}

.text-muted {
  color: #9ca3af;
}

.loading-state {
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
  font-size: 16px;
}

.loading-state i {
  font-size: 32px;
  margin-bottom: 12px;
  display: block;
}

.no-data-message {
  text-align: center;
  padding: 60px 20px;
  color: #9ca3af;
  font-size: 14px;
}

@media (max-width: 768px) {
  .tables-section {
    grid-template-columns: 1fr;
  }

  .date-filter-section {
    flex-direction: column;
    align-items: stretch;
  }

  .quick-filters {
    flex-wrap: wrap;
  }
}
</style>
